/**
 * @description TowTech SO Employees + Pending Matches (LYN-410 / LYN-421).
 *
 * Key features:
 * - Employees list is the page
 * - Pending Matches sits under the list and only renders when rows exist
 * - Company view: assigned employees only
 * - All: assignment company names on each row
 * - Pending: unmatched person + dropdown of SO employees, or Create new
 *
 * @dependencies PackPage, soEmployeesService, AuthContext, PackChromeContext
 *
 * @notes Stored one-hit links never appear. No Assets & Crew. No LynxPay.
 *   Pending Matches is not a sibling nav item.
 */

import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import PackPage from '../../components/layouts/so/PackPage';
import { useAuth } from '../../contexts/AuthContext';
import { usePackChrome } from '../../contexts/PackChromeContext';
import soEmployeesService from '../../services/api/soEmployeesService';

/**
 * @param {object} person
 * @returns {string}
 */
function personLabel(person) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  if (name) return name;
  return person.displayName || 'Unmatched person';
}

/**
 * @param {string[]} names
 * @returns {string}
 */
function companyChip(names) {
  return (names || []).filter(Boolean).join(', ');
}

export default function SoEmployeesPage() {
  const { towtechSoChrome, loading: packLoading } = usePackChrome();
  const { companyScope, activeCompanyId } = useAuth();
  const isAll = companyScope === 'all';

  const [employees, setEmployees] = useState([]);
  const [pendingRows, setPendingRows] = useState([]);
  const [options, setOptions] = useState([]);
  const [picks, setPicks] = useState({});
  const [status, setStatus] = useState('loading');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const [empResult, pendingResult, optionResult] = await Promise.allSettled([
        soEmployeesService.listSoEmployees({ status: 'all', limit: 100 }),
        soEmployeesService.listPendingMatches(),
        soEmployeesService.listEmployeeOptions()
      ]);
      if (empResult.status !== 'fulfilled') {
        setStatus('failed');
        return;
      }
      setEmployees(empResult.value.employees || []);
      setPendingRows(
        pendingResult.status === 'fulfilled'
          ? (pendingResult.value.pendingMatches || [])
          : []
      );
      setOptions(
        optionResult.status === 'fulfilled'
          ? (optionResult.value.employees || [])
          : []
      );
      setStatus('ready');
    } catch (_error) {
      setStatus('failed');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, activeCompanyId, companyScope]);

  const resolveRow = async (pendingId, create) => {
    const employeeId = picks[pendingId];
    if (!create && !employeeId) return;
    setBusyId(pendingId);
    try {
      await soEmployeesService.resolvePendingMatch(pendingId, create
        ? { create: true }
        : { employeeId });
      await load();
    } finally {
      setBusyId(null);
    }
  };

  if (packLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  }
  if (!towtechSoChrome) {
    return <Navigate to="/so/dashboard" replace />;
  }

  return (
    <PackPage title="Employees">
      {status === 'loading' && (
        <p className="text-sm text-gray-500">Loading employees…</p>
      )}
      {status === 'failed' && (
        <p className="text-sm text-gray-500">Couldn&apos;t load employees.</p>
      )}
      {status === 'ready' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200" data-testid="so-employee-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver number</th>
                {isAll ? (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Companies</th>
                ) : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {employees.length === 0 ? (
                <tr>
                  <td colSpan={isAll ? 3 : 2} className="px-4 py-8 text-sm text-gray-500">
                    No employees assigned to this view.
                  </td>
                </tr>
              ) : employees.map((employee) => (
                <tr key={employee.id}>
                  <td className="px-4 py-3 text-sm text-gray-900">{personLabel(employee)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{employee.driverNumber || '—'}</td>
                  {isAll ? (
                    <td className="px-4 py-3 text-sm text-gray-700" data-testid="employee-companies">
                      {companyChip(employee.assignedCompanies) || '—'}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {status === 'ready' && pendingRows.length > 0 && (
        <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="pending-matches">
          <h2 className="px-4 pt-4 text-base font-semibold text-gray-900">Pending Matches</h2>
          <table className="mt-2 min-w-full divide-y divide-gray-200" data-testid="so-pending-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unmatched</th>
                {isAll ? (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Companies</th>
                ) : null}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attach to</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pendingRows.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {personLabel(row)}
                    {row.hitCount > 1 ? (
                      <span className="ml-2 text-xs text-gray-500">Multiple matches</span>
                    ) : (
                      <span className="ml-2 text-xs text-gray-500">No match</span>
                    )}
                  </td>
                  {isAll ? (
                    <td className="px-4 py-3 text-sm text-gray-700" data-testid="pending-companies">
                      {companyChip(row.assignedCompanies) || row.companyName || '—'}
                    </td>
                  ) : null}
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        aria-label={`Attach ${personLabel(row)}`}
                        className="border border-gray-300 rounded-md text-sm px-2 py-1"
                        value={picks[row.id] || ''}
                        onChange={(event) => setPicks((current) => ({
                          ...current,
                          [row.id]: event.target.value
                        }))}
                      >
                        <option value="">Select employee</option>
                        {options.map((option) => (
                          <option key={option.id} value={option.id}>
                            {personLabel(option)}
                            {isAll && option.assignedCompanies?.length
                              ? ` (${companyChip(option.assignedCompanies)})`
                              : ''}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="px-3 py-1 text-sm rounded-md border border-gray-300 text-gray-800 disabled:opacity-50"
                        disabled={!picks[row.id] || busyId === row.id}
                        onClick={() => resolveRow(row.id, false)}
                      >
                        Attach
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1 text-sm rounded-md border border-gray-300 text-gray-800 disabled:opacity-50"
                        disabled={busyId === row.id}
                        onClick={() => resolveRow(row.id, true)}
                      >
                        Create new
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PackPage>
  );
}
